<script lang="ts" setup>
  import { computed, ref, watch } from 'vue';
  import { parseISO, add, format } from "date-fns";
  import { useStore } from 'vuex';

  import { cntProps } from 'vue/applications/frontend/mixins/cnt-component';
  import HttpMethod from 'vue/applications/shared/endpoints/utils/http-method';
  import UniversalEndpoint from '../../endpoints/universal-endpoint';
  import SimpleDatepicker from 'vue/applications/frontend/components/simple-datepicker.vue'
 
  const props = defineProps({...cntProps()});

  const store = useStore();
  const backendData = ref({
    selectedPractice: null,
    selectedCurriculumId: null,
    selectedLicenseClass: null,
    selectedStartAt: null,
    selectedAmount: null,
    comment: "",
    trainee_email: null,
    trainee_first_name: null,
    trainee_last_name: null,
  });
  const sending = ref<boolean>(false);
  const errors = ref<Array<any>>([]);
  const role_type = ref<String | null>(null);

  const availableLicensesForCurriculum = computed(() => {
    if(!backendData.value.selectedCurriculumId) return props.contentItem.widget_data.available_licenses;

    return props.contentItem.widget_data.available_licenses.filter((license: any) => {
      return license.curriculum_id == backendData.value.selectedCurriculumId
    });
  });

  const selectedRole = computed(() => {
    if (
      !props.contentItem ||
      !props.contentItem.widget_data ||
      !props.contentItem.widget_data.roles
    )
      return null;

    return props.contentItem.widget_data.roles.find((role: any) => role.id == role_type.value);
  });

  const selectedLicense = computed(() => {
    if (
      !props.contentItem ||
      !props.contentItem.widget_data ||
      !props.contentItem.widget_data.available_licenses
    )
      return null;

    for (const license of props.contentItem.widget_data.available_licenses) {
      if (license.license_class === backendData.value.selectedLicenseClass) {
        return license;
      }
    }
    return null;
  });

  const selectedCurriculum = computed(() => {
    if (
      !props.contentItem ||
      !props.contentItem.widget_data ||
      !props.contentItem.widget_data.available_curricula
    )
      return null;

    for (const curriculum of props.contentItem.widget_data.available_curricula) {
      if (curriculum.id === backendData.value.selectedCurriculumId) {
        return curriculum;
      }
    }
    return null;
  });

  const needSelectStartDate = computed(() => true);
  const showSelectStartDate = computed(() => needSelectStartDate.value && selectedLicense.value)
  const needSelectAmount = computed(() => selectedLicense.value?.booking_options?.length > 1);
  
  const showSelectAmount = computed(() => (needSelectAmount.value && selectedLicense.value &&
    (!needSelectStartDate.value || backendData.value.selectedStartAt)));
  
  const effectiveSelectedAmount = computed(() => {
    if (backendData.value.selectedAmount) {
      for (const bookingOption of selectedLicense.value.booking_options) {
        if (bookingOption.id === backendData.value.selectedAmount) {
          return bookingOption;
        }
      }
      console.error("No booking option found. This should not happen");
      return null;
    } else if (selectedLicense.value.booking_options.length == 1) {
      return selectedLicense.value.booking_options[0];
    } else {
      return null;
    }
  });

  const showConfirmation = computed(() => (
    selectedLicense.value && backendData.value.trainee_email && backendData.value.trainee_first_name && backendData.value.trainee_last_name &&
    (!needSelectStartDate.value || backendData.value.selectedStartAt) &&
    (!needSelectAmount.value || backendData.value.selectedAmount)));

  // returns the end date, null indicates no end date (i.e. unlimited period of validity)
  const endDate = computed(() => {
    if (
      !backendData.value.selectedStartAt ||
      !selectedLicense.value ||
      !effectiveSelectedAmount.value
    )
      return null;

    if (effectiveSelectedAmount.value.duration.unit == "unlimited") {
      return null;
    }

    const adder: Record<string, number> = {};
    adder[
      effectiveSelectedAmount.value.duration.unit
    ] = effectiveSelectedAmount.value.duration.amount;
    return add(parseISO(backendData.value.selectedStartAt), adder);
  });

  const translateDuration = (duration: string, amount: number) => {
    if (!amount) amount = 100;
    switch (duration) {
      case "years":
        return amount > 1 ? "Jahre" : "Jahr";
      case "months":
        return amount > 1 ? "Monate" : "Monat";
      case "weeks":
        return amount > 1 ? "Wochen" : "Woche";
      default:
        return duration;
    }
  };
  const formatISODate = (dateStr: string) => {
    if (!dateStr) return "unbekannt";
    return formatDate(parseISO(dateStr));
  };
  const formatDate = (date: Date) => {
    if (!date) return "unbekannt";
    return format(date, "dd.MM.yyyy");
  };
  const formatPrice = (price: number) => {
    if (price !== 0 && !price) return "-";

    const cents = price % 100;
    const paddedCents = `${cents}00`.substring(0, 2);

    const euros = (price - cents) / 100;

    return `${euros},${paddedCents}`;
  };
  const onBookLicense = async() => {
    if (sending.value) return;

    let skipSendingFlip = false;

    try {
      sending.value = true;
      errors.value = [];

      const endpoint = new UniversalEndpoint(store);
      const response = await endpoint.request(
        props.contentItem.widget_data.license_booking_user_path,
        HttpMethod.POST,
        { selectedPractice: backendData.value.selectedPractice ?? undefined,
          selectedCurriculumId: backendData.value.selectedCurriculumId ?? undefined,
          selectedLicenseClass:  backendData.value.selectedLicenseClass ?? undefined,
          selectedStartAt:  backendData.value.selectedStartAt ?? undefined,
          selectedAmount:  backendData.value.selectedAmount ?? undefined,
          comment: backendData.value.comment ?? '',
          trainee_email: backendData.value.trainee_email ?? undefined,
          trainee_first_name: backendData.value.trainee_first_name ?? undefined,
          trainee_last_name: backendData.value.trainee_last_name ?? undefined,
        },
      );

      if (response && response.data) {
        if (response.data.redirect) {
          location.href = response.data.redirect;
          skipSendingFlip = true;
        } else {
          const errMessage = response.data.error ?? `Es ist etwas schief gelaufen. Bitte versuchen Sie es erneut. Falls der Fehler wiederholt auftritt, wenden Sie sich bitte an ${props.contentItem.widget_data.support_mail}`;
          errors.value.push(errMessage);
        }
      }
    } finally {
      sending.value = skipSendingFlip;
    }
  };

  watch(selectedLicense, () => {
    backendData.value.selectedAmount = null;
  });

  watch(() => backendData.value.selectedCurriculumId, () => {
    backendData.value.selectedLicenseClass = null;
  });

  watch(
    () => props.contentItem.widget_data.available_curricula,
    (availableCurricula: any) => {
      if (availableCurricula.length === 1) {
        backendData.value.selectedCurriculumId = availableCurricula[0].id;
      }
    },
    { immediate: true }
    );
</script>

<template>
  <div class="components__cnt-license">
    <template v-if="contentItem">
      <div
        class="license__not-logged-in"
        v-if="contentItem.widget_data.login_path"
      >
        Sie müssen sich zunächst
        <a v-bind:href="contentItem.widget_data.login_path"
          >einloggen oder registrieren</a
        >, um die Bestellung einer Lizenz durchführen zu können.
      </div>
      <div v-else>
        <div>
          <label class="license__option">
            <select v-model="role_type">
              <option v-bind:value="null">Bitte auswählen:</option>
              <option
                v-for="role in contentItem.widget_data.roles"
                v-bind:key="role.id"
                v-bind:value="role.id"
              >
                {{ role.label }}
              </option>
            </select>
            <div>
              <strong>
                <template v-if="role_type">{{ selectedRole.label }}</template>
                <template v-else>Ich möchte {{ contentItem.widget_data.app_flavor_name }} nutzen als</template>
              </strong>
            </div>
          </label>
        </div>

        <template v-if="role_type == 'trainee'">
          <p>Die Lizenzbestellung wird von Ihrer/Ihrem Weiterbildungsberechtigten vorgenommen. Bis zur Genehmigung der Lizenz können Sie kostenfrei die Schnupperversion verwenden.</p>
        </template>
        <template v-else-if="role_type != null">
          <div>
            <label class="license__option">
              <template v-if="contentItem.widget_data.available_practices.length > 0">
                <select v-model="backendData.selectedPractice">
                  <option v-bind:value="null" disabled>Bitte auswählen</option>
                  <option
                    v-for="practice in contentItem.widget_data.available_practices"
                    v-bind:key="practice"
                    v-bind:value="practice"
                  >
                    {{ practice }}
                  </option>
                </select>
                <div>
                  <strong>
                    <template v-if="backendData.selectedPractice">{{ backendData.selectedPractice }}</template>
                    <template v-else>Aus- und Weiterbildungsstätte</template>
                  </strong>
                </div>
              </template>
              <template v-else>
                <div>
                  <strong>Aus- und Weiterbildungsstätte</strong>
                  <div><a v-bind:href="contentItem.widget_data.practice_data_path">Bitte geben Sie hier zunächst die Daten Ihrer Aus- und Weiterbildungsstätte an.</a></div>
                </div>
              </template>
            </label>
          </div>

          <template v-if="backendData.selectedPractice">
            <div>
              <label class="license__option">
                <template v-if="contentItem.widget_data.available_curricula.length > 1">
                  <select v-model="backendData.selectedCurriculumId">
                    <option v-bind:value="null">Bitte auswählen:</option>
                    <option v-for="curriculum in contentItem.widget_data.available_curricula" v-bind:key="curriculum.id" v-bind:value="curriculum.id">
                      {{ curriculum.name }}
                    </option>
                  </select>
                </template>
                <div>
                  <strong>
                    <template v-if="selectedCurriculum">{{selectedCurriculum.name}}</template>
                    <template v-else>Curriculum</template>
                  </strong>
                  <div v-if="selectedCurriculum && selectedCurriculum.short_description">{{ selectedCurriculum.short_description }}</div>
                </div>
              </label>
            </div>
            <template v-if="backendData.selectedCurriculumId">
              <p v-if="availableLicensesForCurriculum.length <= 0">
                Aktuell steht keine Lizenz für das Curriculum zur Verfügung.
              </p>
              <div
                v-for="license in availableLicensesForCurriculum"
                v-bind:key="license.license_class"
              >
                <label
                  class="license__option"
                  v-bind:class="{
                    'license__option--disabled': license.disabled,
                    'license__option--selected': license === selectedLicense,
                  }"
                >
                  <input
                    type="radio"
                    v-bind:disabled="license.disabled || sending"
                    v-bind:value="license.license_class"
                    v-model="backendData.selectedLicenseClass"
                  />
                  <div>
                    <strong>{{ license.title }}</strong>
                    <div v-if="license.description">{{ license.description }}</div>
                    <div v-if="license.disabled">
                      <i>nicht verfügbar</i>
                    </div>
                  </div>
                </label>
              </div>

              <div
                v-if="selectedLicense"
                class="license__input-container"
              >
                <label>
                  <strong>Für welche Ärztin in Weiterbildung / welchen MFA-Azubi soll die Lizenz verwendet werden?</strong>
                  <div class="trainee-field">
                      <label for="trainee_email">E-Mail-Adresse der ÄiW / des MFA-Azubi</label>
                      <input type="email" id="trainee_email" v-model="backendData.trainee_email" placeholder="E-Mail-Adresse" required>
                  </div>
                  <div class="trainee-field">
                    <label for="trainee_first_name">Vorname der ÄiW / des MFA-Azubi</label>
                    <input type="text" id="trainee_first_name" v-model="backendData.trainee_first_name" placeholder="Vorname" required>
                  </div>
                  <div class="trainee-field">
                    <label for="trainee_last_name">Nachname der ÄiW / des MFA-Azubi</label>
                    <input type="text" id="trainee_last_name" v-model="backendData.trainee_last_name" placeholder="Nachname" required>
                  </div>
                </label>
              </div>

              <div
                v-if="showSelectStartDate"
                class="license__select-start-date license__input-container"
              >
                <label
                  ><strong
                    >Wählen Sie den gewünschten Startzeitpunkt der Lizenz aus</strong
                  >
                  <div class="license__input-wrapper">
                    <SimpleDatepicker
                      future
                      v-bind:min-date="new Date()"
                      v-model="backendData.selectedStartAt"
                      skip-default-value
                      v-bind:disabled="sending"
                    ></SimpleDatepicker>
                  </div>
                </label>
              </div>

              <div
                v-if="showSelectAmount"
                class="license__select-amount license__input-container"
              >
                <label
                  ><strong>Wählen Sie die gewünschte Laufzeit der Lizenz aus</strong>
                  <div class="license__input-wrapper">
                    <select
                      v-bind:disabled="sending"
                      v-model="backendData.selectedAmount"
                    >
                      <option
                        v-for="bookingOption in selectedLicense.booking_options"
                        v-bind:key="bookingOption.id"
                        v-bind:value="bookingOption.id"
                      >
                        {{ bookingOption.duration.amount }}
                        {{
                          translateDuration(
                            bookingOption.duration.unit,
                            bookingOption.duration.amount,
                          )
                        }}
                      </option>
                    </select>
                  </div>
                </label>
              </div>

              <div class="license__confirmation" v-if="showConfirmation">
                <h2>Zusammenfassung</h2>
                <div
                  class="license__input-container license__input-container--no-top-margin license__input-container--with-bottom-margin"
                >
                  <label>
                    <strong>Anmerkungen zur Buchung</strong>
                    <div class="license__input-wrapper">
                      <textarea
                        rows="5"
                        v-bind:disabled="sending"
                        placeholder="Anmerkungen..."
                        v-model="backendData.comment"
                      ></textarea>
                    </div>
                  </label>
                </div>
                <p>
                  Ich möchte die Option
                  <strong>&bdquo;{{ selectedLicense.title }}&ldquo;</strong> ab dem
                  <strong>{{ formatISODate(backendData.selectedStartAt) }}</strong>
                  <template v-if="endDate">
                    bis zum <strong>{{ formatDate(endDate) }}</strong>
                  </template>
                  <template v-if="effectiveSelectedAmount.price > 0">
                    für einmalig
                    <strong
                      >{{ formatPrice(effectiveSelectedAmount.price) }} &euro;</strong
                    >
                    (inkl. 19% MwSt.) kostenpflichtig</template
                  ><template v-else>kostenlos</template> bestellen.
                </p>
                <div v-if="errors">
                  <div
                    v-for="(error, index) in errors"
                    v-bind:key="index"
                    class="booking__error"
                  >
                    {{ error }}
                  </div>
                </div>
                <div>
                  <button
                    class="button"
                    v-bind:class="{ 'button--disabled': sending }"
                    v-on:click="onBookLicense"
                  >
                    <template v-if="sending">
                      Bestellung wird durchgeführt... </template
                    ><template v-else>
                      <template v-if="effectiveSelectedAmount.price > 0"
                        >Kostenpflichtig</template
                      >
                      bestellen</template
                    >
                  </button>
                </div>
              </div>
            </template>
          </template>
        </template>
      </div>
    </template>
  </div>
</template>

<style lang="scss" scoped>
.components__cnt-license {
  @import "~stylesheets/frontend/variables/colors";

  .license__not-logged-in {
    padding: 20px;
    background-color: $color-black-very-light;
  }

  .license__option {
    display: block;
    padding: 20px;
    margin: 20px 0;
    border: 1px solid $color-green;

    display: flex;
    align-items: center;

    input, select {
      margin-right: 20px;
    }
  }

  .license__option--selected {
    background-color: $color-green;
    color: $color-inverted;
  }

  .license__option--disabled {
    background-color: $color-black-vertical-line;
    border: 1px solid $color-black-vertical-line;
  }

  .license__input-container {
    margin-top: 40px;
  }

  .license__input-wrapper {
    margin-top: 5px;

    textarea {
      width: 100%;
    }
  }

  .license__input-container--no-top-margin {
    margin-top: 0;
  }

  .license__input-container--with-bottom-margin {
    margin-bottom: 20px;
  }

  .license__select-amount {
    select {
      padding: 1px 2px;
    }
  }

  .license__confirmation {
    margin-top: 40px;
  }

  .booking__error {
    margin-bottom: 15px;
    color: red;
    font-weight: bold;
  }

  .trainee-field {
    margin-top: 10px;

    label {
      display: inline-block;
      width: 300px;
    }

    input {
      display: inline-block;
      width: 300px;
    }
  }

  .button {
    background-color: $color-green;
    color: $color-inverted;

    padding: 10px 20px;
    cursor: pointer;
    box-shadow: none;
    outline: none;
    border: none;
  }

  .button--disabled {
    background-color: $color-black-inactive;
    cursor: not-allowed;
  }
}
</style>
